import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Endpoint, EndpointService } from '@services/endpoints';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, first, map, take, tap } from 'rxjs/operators';

import { I18nService } from '../../i18n/services';
import { StorageService, StorageType } from '../storage';
import {
	IManagedUser,
	IManagedUserUpdate,
	IUserSearchFields,
	IUsersEventStream,
	IUsersSearchQuery,
	IUsersSearchResults,
	IUsersServiceData,
	IUserTerms,
	UsersEventType,
	UsersStorageKeys,
	IAvailableRoles,
	IUserRoleSources
} from './users.models';

@Injectable({
	providedIn: 'root'
})
export class UsersService extends OnDestroyMixin {
	private usersEventSource = new Subject<IUsersEventStream>();
	public eventStream = this.usersEventSource.asObservable();

	private searchQuery: IUsersSearchQuery = {
		query: {},
		ranges: {},
		loginTimezone: 'Australia/Sydney',
		createdTimezone: 'Australia/Sydney',
		emailVerified: 'all',
		gender: 'all',
		role: 'all',
		billingCountry: 'all',
		pageSize: 25,
		pageNumber: 0,
		sortColumn: 'email',
		sortOrder: 'asc'
	};

	public set userSearchQuery(query: IUsersSearchQuery) {
		this.searchQuery = { ...query };
		this.storageService.setItem(StorageType.sessionStorage, UsersStorageKeys.usersSearchQuery, this.searchQuery);
	}

	public get userSearchQuery(): IUsersSearchQuery {
		const query = this.storageService.getItem(StorageType.sessionStorage, UsersStorageKeys.usersSearchQuery);

		if (query) {
			this.searchQuery = { ...this.searchQuery, ...query };

			for (const [key, value] of Object.entries(this.searchQuery.ranges)) {
				if (value[0]) {
					this.searchQuery.ranges[key][0] = parseInt(value[0] as any as string, 10);
				}
				if (value[1]) {
					this.searchQuery.ranges[key][1] = parseInt(value[1] as any as string, 10);
				}
			}
		}

		return this.searchQuery;
	}

	public data: IUsersServiceData = {
		results: {
			cnt: 0,
			offset: 0,
			results: []
		},
		terms: {}
	};

	constructor(
		private endpointService: EndpointService,
		private http: HttpClient,
		private storageService: StorageService,
		private i18nService: I18nService
	) {
		super();
	}

	public clearCache() {
		this.data.results = {
			cnt: 0,
			offset: 0,
			nextPageToken: null,
			previousPageToken: null,
			results: []
		};
		this.data.terms = {};
	}

	public getUsers(
		search: IUserSearchFields,
		pageSize: number,
		pageNumber: number,
		sortColumn: string,
		sortOrder: string,
		force = false
	): Observable<IUsersSearchResults> {
		const refreshCache =
			force ||
			JSON.stringify({
				...search,
				pageSize,
				pageNumber,
				sortColumn,
				sortOrder
			}) !== JSON.stringify(this.userSearchQuery) ||
			this.data.results.results.length === 0;

		const searchEndpoint = search.role && search.role !== 'all' ? Endpoint.usersRoleSearch : Endpoint.usersSearch;
		const url = `${this.endpointService.url(`${searchEndpoint}/${pageSize * pageNumber}/${pageSize}`)}`;

		this.userSearchQuery = {
			...search,
			pageSize,
			pageNumber,
			sortColumn,
			sortOrder
		};

		const payload = {
			user: { ...search.query },
			role: {},
			order: [{ field: sortColumn, order: sortOrder }],
			ranges: { ...search.ranges }
		};

		if (search.emailVerified === 'verified') {
			payload.user['validated'] = true;
		} else if (search.emailVerified === 'unverified') {
			payload.user['validated'] = false;
		}

		if (search.gender && search.gender !== 'all') {
			payload.user['gender'] = search.gender;
		}

		if (search.role && search.role !== 'all') {
			payload.role['roleId'] = search.role;
			if (pageNumber === 0 || this.data.results.offset < (pageNumber * pageSize)) {
				payload.role['cursorToken'] = this.data.results.nextPageToken;
				payload.role['forward'] = true;
				this.data.results.currentPageToken = this.data.results.nextPageToken;
				this.data.results.currentPageForward = true;
			} else if (this.data.results.offset > (pageNumber * pageSize)) {
				payload.role['cursorToken'] = this.data.results.previousPageToken;
				payload.role['forward'] = false;
				this.data.results.currentPageToken = this.data.results.previousPageToken;
				this.data.results.currentPageForward = false;
			} else {
				// same page...
				payload.role['cursorToken'] = this.data.results.currentPageToken;
				payload.role['forward'] = this.data.results.currentPageForward;
			}
		}

		if (search.billingCountry && search.billingCountry !== 'all') {
			payload.user['billingCountry'] = search.billingCountry;
		}

		if (Object.keys(payload.user).length === 0 && Object.keys(payload.ranges).length === 0 && Object.keys(payload.role).length === 0) {
			this.data.results = {
				cnt: 0,
				offset: 0,
				nextPageToken: null,
				previousPageToken: null,
				results: []
			};

			return of(this.data.results);
		} else if (refreshCache) {
			return this.http.post<IUsersSearchResults>(url, payload).pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				map((res: IUsersSearchResults) => {
					if (!res) {
						throw Error('error');
					} else {
						const cnt = this.data.results.cnt || res.cnt || 0;
						this.data.results = res;
						if (cnt > this.data.results.cnt) {
							this.data.results.cnt = cnt;
						}
						return res;
					}
				}),
				first()
			);
		} else {
			return of(this.data.results);
		}
	}

	public getAcceptedTerms(userId: number | string, clusterId: string, force = false, history = true): Observable<IUserTerms[]> {
		if (force || !this.data.terms?.[clusterId]?.[userId]) {
			return this.http
				.get<IUserTerms[]>(`${this.endpointService.url(Endpoint.usersAcceptedTerms).replace('%s', `${userId}`)}?latestOnly=${!history}`)
				.pipe(
					map((res: IUserTerms[]) => {
						if (!this.data.terms?.[clusterId]) {
							this.data.terms[clusterId] = {};
						}

						this.data.terms[clusterId][userId] = res;

						return res;
					}),
					first()
				);
		} else {
			return of(this.data.terms[clusterId][userId]);
		}
	}

	public acceptTerm(userId: number | string, clusterId: string, termsId: string): Observable<any> {
		return this.http
			.post(
				`${this.endpointService.url(Endpoint.usersAcceptTerms).replace('%s', `${userId}`)}?termsId=${termsId}`,
				null
			)
			.pipe(
				map((res: boolean) => {
					if (res) delete this.data.terms[clusterId][userId];

					return res;
				}),
				first()
			);
	}

	public revokeTermAcceptance(userId: number | string, clusterId: string, termsId: string): Observable<any> {
		return this.http
			.post(
				`${this.endpointService.url(Endpoint.usersRevokeTerms).replace('%s', `${userId}`)}?termsId=${termsId}`,
				null
			)
			.pipe(
				map((res: boolean) => {
					if (res && this.data.terms?.[clusterId]?.[userId]) {
						const index = this.data.terms[clusterId][userId].findIndex((terms) => terms.termsId === termsId);
						const versions = Object.keys(this.data.terms[clusterId][userId][index].versions).sort((a, b) => b.localeCompare(a));

						if (versions.length > 1) {
							delete this.data.terms[clusterId][userId][index].versions[versions[0]];
						} else {
							this.data.terms[clusterId][userId] = this.data.terms[clusterId][userId].filter(
								(terms) => terms.termsId !== termsId
							);
						}
					}

					return res;
				}),
				first()
			);
	}

	/**
	 * Validates if an email exists or not and if needed will migrate the email from Gigya to Cognito.
	 *
	 * @param clientId Client ID
	 * @param clusterId Cluster ID
	 * @param email User email to validate and migrate
	 */
	public usersMigrateGigya(email: string): Observable<boolean> {
		return this.http.get<boolean>(`${this.endpointService.url(Endpoint.usersMigrateGigya)}?email=${encodeURIComponent(email)}`).pipe(
			tap({
				error: (error) => this.processError(error)
			}),
			first()
		);
	}

	public sendResetPasswordEmail(email: string): Observable<any> {
		return this.http.get(`${this.endpointService.url(Endpoint.usersSendPasswordResetEmail)}?email=${encodeURIComponent(email)}`).pipe(
			tap({
				error: (error) => this.processError(error)
			}),
			first()
		);
	}

	public recoverPassword(email: string, password: string, token: string): Observable<any> {
		return this.http.post(
			`${this.endpointService.url(Endpoint.usersRecoverPassword)}`,
			{
				email,
				password,
				token
			}).pipe(
			tap({
				error: (error) => this.processError(error)
			}),
			first()
		);
	}

	public sendVerificationEmail(email: string): Observable<any> {
		return this.http.get(`${this.endpointService.url(Endpoint.usersSendVerificationEmail)}?email=${encodeURIComponent(email)}`).pipe(
			tap({
				error: (error) => this.processError(error)
			}),
			first()
		);
	}

	public setEmailVerification(userId: number, email: string): Observable<IManagedUser> {
		return this.http
			.get<IManagedUser>(`${this.endpointService.url(Endpoint.usersSetEmailVerification)}?email=${encodeURIComponent(email)}`)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				map(() => {
					const index = this.data.results.results.findIndex((cachedUser: IManagedUser) => cachedUser.id === userId);
					this.data.results.results[index].validated = true;

					this.usersEventSource.next({
						action: UsersEventType.loaded,
						users: this.data.results.results
					});

					return this.data.results.results[index];
				}),
				first()
			);
	}

	// TODO currently not used
	public deleteUser(userId: number, email: string): Observable<boolean> {
		return this.http.delete<boolean>(`${this.endpointService.url(Endpoint.usersDeleteUser)}?email=${encodeURIComponent(email)}`).pipe(
			tap({
				error: (error) => this.processError(error)
			}),
			map(() => {
				const index = this.data.results.results.findIndex((cachedUser: IManagedUser) => cachedUser.id === userId);
				this.data.results.results.splice(index, 1);
				this.data.results.cnt -= 1;

				return true;
			}),
			first()
		);
	}

	public updateUsersPassword(email: string, password: string) {
		return this.http.post(
			`${this.endpointService.url(Endpoint.usersSetNewPassword)}?email=${encodeURIComponent(email)}`,
			{ newPassword: password })
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				first()
			);
	}

	public suspendUserAccount(userId: number, cognitoUUID: string): Observable<IManagedUser> {
		return this.http
			.post<IManagedUser>(`${this.endpointService.url(Endpoint.usersSuspendAccount)}?uid=${cognitoUUID}`, null)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				map((res: IManagedUser) => {
					const index = this.data.results.results.findIndex((cachedUser: IManagedUser) => cachedUser.id === userId);
					this.data.results.results[index] = res;

					this.usersEventSource.next({
						action: UsersEventType.loaded,
						users: this.data.results.results
					});

					return this.data.results.results[index];
				}),
				first()
			);
	}

	public unsuspendUserAccount(userId: number, cognitoUUID: string): Observable<IManagedUser> {
		return this.http
			.post<IManagedUser>(`${this.endpointService.url(Endpoint.usersUnsuspendAccount)}?uid=${cognitoUUID}`, null)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				map((res: IManagedUser) => {
					const index = this.data.results.results.findIndex((cachedUser: IManagedUser) => cachedUser.id === userId);
					this.data.results.results[index] = res;

					this.usersEventSource.next({
						action: UsersEventType.loaded,
						users: this.data.results.results
					});

					return this.data.results.results[index];
				}),
				first()
			);
	}

	public updateUserAttributes(
		userId: string | number,
		cognitoUUID: string,
		attributes: IManagedUserUpdate
	): Observable<IManagedUser> {
		return this.http
			.post<IManagedUser>(`${this.endpointService.url(Endpoint.usersUpdateAttributes)}?uid=${cognitoUUID}`, attributes)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				map((user: IManagedUser) => {
					const index = this.data.results.results.findIndex((cachedUser: IManagedUser) => cachedUser.id === userId);
					this.data.results.results[index] = user;

					this.usersEventSource.next({
						action: UsersEventType.loaded,
						users: this.data.results.results
					});

					return user;
				}),
				first()
			);
	}

	public getAvailableRoles(): Observable<IAvailableRoles> {
		return this.http.get<IAvailableRoles>(`${this.endpointService.url(Endpoint.usersAvailableRoles)}`)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				first()
			);
	}

	public getUserRoles(userId: number, email: string): Observable<string[]> {
		return this.http.get<string[]>(`${this.endpointService.url(Endpoint.usersRoles)}?email=${encodeURIComponent(email)}`)
			.pipe(
                tap({
                    error: (error) => this.processError(error)
                }),
                first()
            );
	}

	public addUserRole(userId: number, email: string, role: string) {
		return this.http.put(`${this.endpointService.url(Endpoint.usersRoles)}?email=${encodeURIComponent(email)}&role=${role}`, null)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				first()
			);
	}

	public removeUserRole(userId: number, email: string, role: string) {
		return this.http.delete(`${this.endpointService.url(Endpoint.usersRoles)}?email=${encodeURIComponent(email)}&role=${role}`)
			.pipe(
				tap({
					error: (error) => this.processError(error)
				}),
				first()
			);
	}

	public getUserRolesSources(userId: number, email: string): Observable<IUserRoleSources> {
		return this.http.get<IUserRoleSources>(`${this.endpointService.url(Endpoint.usersRolesSources)}?email=${encodeURIComponent(email)}`)
            .pipe(
                tap({
                    error: (error) => this.processError(error)
                }),
                first()
            );
    }

	private processError(error: HttpErrorResponse): HttpErrorResponse {
		if (error?.status === 401 && error?.error?.message === 'Unauthorized') {
			error.error.errorMessage = this.i18nService.get(
				'The selected app key is not authorized to perform this action. Select another key and try again.',
				'app.prompt.users.app-key-unauthorized'
			);
		}

		return error;
	}
}
